import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Capacitor } from "@capacitor/core";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** No-render helper that hooks into Capacitor's native APIs ONCE on mount.
 *
 *  - Deep links: when iOS/Android receives a Universal/App Link
 *    (e.g. https://weathervaneapp.net/manage/{token}), Capacitor delivers
 *    the URL via App.appUrlOpen. We strip the host and route to the
 *    matching path inside react-router so the user lands on the in-app
 *    page instead of triggering a full-page reload.
 *
 *  - Push notifications: requests permission, registers with APNs/FCM,
 *    and POSTs the resulting device token to /api/push/subscribe so
 *    storm-warning dispatch reaches the device. Web push is handled
 *    separately by /services/pushNotifications.js — this only runs on
 *    the native shells.
 *
 *  Renders nothing on the web (Capacitor.isNativePlatform() returns false).
 *  All native plugin imports are dynamic so the web bundle doesn't try
 *  to resolve them.
 */
export default function CapacitorBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor?.isNativePlatform?.()) return;
    let appUrlListener;
    let pushTokenListener;
    let pushReceivedListener;
    let pushActionListener;

    (async () => {
      // ---- Deep links ----
      try {
        const { App } = await import("@capacitor/app");
        appUrlListener = await App.addListener("appUrlOpen", (event) => {
          try {
            const u = new URL(event.url);
            const pathWithQuery = `${u.pathname}${u.search || ""}${u.hash || ""}`;
            if (pathWithQuery && pathWithQuery !== "/") {
              navigate(pathWithQuery, { replace: false });
            }
          } catch (e) {
            // Ignore malformed URLs — they were never going to route anyway.
          }
        });
      } catch (e) {
        console.warn("CapacitorBridge: appUrlOpen wiring failed", e);
      }

      // ---- Push notifications (native APNs/FCM) ----
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const perm = await PushNotifications.checkPermissions();
        let granted = perm.receive === "granted";
        if (!granted) {
          const r = await PushNotifications.requestPermissions();
          granted = r.receive === "granted";
        }
        if (granted) {
          await PushNotifications.register();
          // Token comes asynchronously after register().
          pushTokenListener = await PushNotifications.addListener(
            "registration",
            async (token) => {
              const platform = Capacitor.getPlatform(); // "ios" | "android"
              try {
                await axios.post(
                  `${API}/push/native/register`,
                  { token: token.value, platform },
                  { withCredentials: true },
                );
              } catch (err) {
                console.warn("native push register POST failed", err);
              }
            },
          );
          // Foreground push — log it; the OS shows the banner via plugin config.
          pushReceivedListener = await PushNotifications.addListener(
            "pushNotificationReceived",
            (n) => console.log("push received", n),
          );
          // User tapped the notification.
          pushActionListener = await PushNotifications.addListener(
            "pushNotificationActionPerformed",
            (action) => {
              const url = action?.notification?.data?.url;
              if (url) navigate(url, { replace: false });
            },
          );
        }
      } catch (e) {
        console.warn("CapacitorBridge: push wiring failed", e);
      }
    })();

    return () => {
      try { appUrlListener?.remove?.(); } catch {}
      try { pushTokenListener?.remove?.(); } catch {}
      try { pushReceivedListener?.remove?.(); } catch {}
      try { pushActionListener?.remove?.(); } catch {}
    };
  }, [navigate]);

  return null;
}
